import { ComponentMultiStyleConfig } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';
import { mode, getColor } from '@chakra-ui/theme-tools';

import { InputProps } from './Input';

import { getFocusedOutlineShadow } from 'theme/shadows';

export const inputIconElementPaddingMap = {
  xs: '0.5em',
  sm: '0.6em',
  md: '0.6em',
  lg: '0.75em'
};
const inputGroupPaddingMap = {
  xs: '24px',
  sm: '32px',
  md: '40px',
  lg: '48px'
};

const inputBorderRadiusMap = {
  xs: '4px',
  sm: '8px',
  md: '8px',
  lg: '12px'
};
const inputPaddingMap = {
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '16px'
};

const getFocusedStyles = (props: StyleFunctionProps) => {
  if (props.isDisabled) return {};

  if (props.isInvalid) {
    return {
      boxShadow: getFocusedOutlineShadow(props),
      border: mode(
        `1px solid ${getColor(props?.theme, 'red.darker')}`,
        `1px solid ${getColor(props?.theme, 'red.light')}`
      )(props),
      bg: mode('silver.lighter', 'black.base')(props)
    };
  }

  return {
    boxShadow: getFocusedOutlineShadow(props),
    border: `1px solid ${getColor(props.theme, 'blue.base')}`,
    bg: mode('silver.lighter', 'black.base')(props)
  };
};
const getInvalidStyles = (props: StyleFunctionProps) => ({
  boxShadow: 'none',
  border: mode(
    `1px solid ${getColor(props?.theme, 'red.darker')}`,
    `1px solid ${getColor(props?.theme, 'red.light')}`
  )(props)
});

const inputLeftAndRightElementsSelector = '.chakra-input__left-element, .chakra-input__right-element';

const webkitSearchDecoration = `&::-webkit-search-decoration,
&::-webkit-search-cancel-button,
&::-webkit-search-results-button,
&::-webkit-search-results-decoration`;

type InputThemeProps = InputProps & {
  hasLeftElement: boolean;
  hasRightElement: boolean;
  hasLeftAddon: boolean;
  hasRightAddon: boolean;
  isDisabled: boolean;
};

const InputTheme: ComponentMultiStyleConfig = {
  parts: ['field, addon', 'group'],
  baseStyle: (props: InputThemeProps & StyleFunctionProps) => ({
    field: {
      _disabled: {
        opacity: 'initial',
        borderColor: 'transparent',
        border: 'none',
        cursor: 'not-allowed'
      },

      [webkitSearchDecoration]: {
        display: 'none'
      }
    },
    addon: {
      _light: {
        height: 'auto',
        bg: 'silver.base',
        borderColor: 'grey.lightest',
        color: 'black.lighter'
      },
      _dark: {
        height: 'auto',
        bg: 'black.lighter',
        borderColor: 'black.lightest',
        color: 'silver.light'
      }
    },
    group: {
      display: 'flex',
      position: 'relative',
      width: '100%',
      '.chakra-input': {
        paddingLeft: props.hasLeftElement ? inputGroupPaddingMap[props?.size || 'md'] : 'var(--chakra-space-4)',
        paddingRight: props.hasRightElement ? inputGroupPaddingMap[props?.size || 'md'] : 'var(--chakra-space-4)',
        ...(props.hasLeftAddon ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 'none' } : {}),
        ...(props.hasRightAddon ? { borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none' } : {})
      },
      [inputLeftAndRightElementsSelector]: {
        padding: inputIconElementPaddingMap[props?.size || 'md'],

        svg: {
          width: '100%',
          height: '100%'
        },

        color: props.isDisabled ? 'grey.lighter' : 'black.lighter',
        _dark: {
          color: props.isDisabled ? 'grey.lighter' : 'silver.light'
        },

        '.chakra-button': {
          width: 'auto',
          height: 'auto',
          minWidth: 'auto',

          '& svg': {
            width: '100%',
            height: '100%'
          },
          '&:hover': {
            cursor: 'pointer',
            background: 'none'
          },
          '&:active': {
            boxShadow: 'none'
          },
          '&:focus': {
            boxShadow: 'none'
          }
        }
      }
    }
  }),
  variants: {
    basic: (props: StyleFunctionProps) => ({
      field: {
        background: mode('silver.lighter', 'black.light')(props),
        borderColor: mode('grey.lightest', 'black.lightest')(props),
        borderWidth: '1px',
        color: mode('black.lighter', 'silver.light')(props),
        _disabled: {
          bg: mode('silver.light', 'black.light')(props),
          color: mode('grey.light', 'grey.lighter')(props)
        },
        _placeholder: {
          color: mode('grey.light', 'grey.lighter')(props)
        },
        _hover: {
          bg: !props.isDisabled && mode('silver.lighter', 'black.light')(props),
          borderColor: mode('grey.lighter', 'grey.base')(props)
        },
        _focus: getFocusedStyles(props),
        _active: getFocusedStyles(props),
        _invalid: getInvalidStyles(props)
      }
    }),
    outline: (props: StyleFunctionProps) => ({
      field: {
        borderColor: mode('silver.base', 'black.lightest')(props),
        color: mode('black.lighter', 'silver.light')(props),
        _disabled: {
          bg: mode('silver.light', 'black.light')(props),
          color: mode('grey.light', 'grey.lighter')(props)
        },
        _placeholder: {
          color: mode('grey.light', 'grey.lighter')(props)
        },
        _focus: getFocusedStyles(props),
        _active: getFocusedStyles(props),
        _invalid: getInvalidStyles(props)
      }
    }),
    filled: (props: StyleFunctionProps) => ({
      field: {
        border: '1px solid transparent',
        bg: mode('silver.light', 'black.base')(props),
        color: mode('black.light', 'silver.light')(props),
        _disabled: {
          bg: mode('silver.light', 'black.light')(props),
          color: mode('grey.light', 'grey.lighter')(props)
        },
        _hover: {
          bg: !props.isDisabled && mode('silver.base', 'black.lighter')(props)
        },
        _placeholder: {
          color: mode('grey.light', 'grey.lighter')(props)
        },
        _focus: getFocusedStyles(props),
        _active: getFocusedStyles(props),
        _invalid: getInvalidStyles(props)
      }
    }),
    ghost: (props: StyleFunctionProps) => ({
      field: {
        bg: 'transparent',
        border: '1px solid transparent',
        color: mode('black.lighter', 'silver.light')(props),
        _placholder: {
          color: mode('grey.light', 'grey.lighter')(props)
        },
        _disabled: {
          color: mode('grey.light', 'grey.lighter')(props)
        },
        _hover: {
          bg: !props.isDisabled && mode('silver.light', 'black.lighter')(props)
        },
        _focus: getFocusedStyles(props),
        _active: getFocusedStyles(props),
        _invalid: getInvalidStyles(props)
      }
    }),
    unstyled: (props: StyleFunctionProps) => ({
      field: {
        bg: 'transparent',
        color: mode('grey.light', 'grey.lighter')(props),
        _focus: null,
        _active: null
      }
    })
  },
  sizes: {
    xs: {
      field: {
        height: '24px',
        lineHeight: '24px',
        borderRadius: inputBorderRadiusMap.xs,
        fontSize: '12px',
        paddingInlineStart: inputPaddingMap.xs,
        paddingInlineEnd: inputPaddingMap.xs
      },
      addon: {
        borderRadius: inputBorderRadiusMap.xs
      }
    },
    sm: {
      field: {
        height: '32px',
        lineHeight: '32px',
        borderRadius: inputBorderRadiusMap.sm,
        fontSize: '14px',
        paddingInlineStart: inputPaddingMap.sm,
        paddingInlineEnd: inputPaddingMap.sm
      },
      addon: {
        borderRadius: inputBorderRadiusMap.sm
      }
    },
    md: {
      field: {
        height: '40px',
        lineHeight: '40px',
        borderRadius: inputBorderRadiusMap.md,
        fontSize: '16px',
        paddingInlineStart: inputPaddingMap.md,
        paddingInlineEnd: inputPaddingMap.md
      },
      addon: {
        borderRadius: inputBorderRadiusMap.md
      }
    },
    lg: {
      field: {
        height: '48px',
        lineHeight: '48px',
        borderRadius: inputBorderRadiusMap.lg,
        fontSize: '16px',
        paddingInlineStart: inputPaddingMap.lg,
        paddingInlineEnd: inputPaddingMap.lg
      },
      addon: {
        borderRadius: inputBorderRadiusMap.lg
      }
    }
  }
};

export default InputTheme;
